import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Clock, DollarSign, Calendar, User, Plus } from "lucide-react";
import { getCourseById } from "@/utils/firestoreService";
import { Course } from "@/types/courseTypes";
import { useToast } from "@/hooks/use-toast";
import { useFirebase } from "@/contexts/FirebaseContext";
import { Enrollment } from "@/utils/firestoreService";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { format } from "date-fns";
import ScheduleClassDialog from "@/components/scheduling/ScheduleClassDialog";

const PracticalCourseDetails = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const [loading, setLoading] = useState(false);
  const [course, setCourse] = useState<Course | null>(null);
  const [enrollment, setEnrollment] = useState<Enrollment | null>(null);
  const [isSchedulingOpen, setIsSchedulingOpen] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { currentUser, getEnrollmentsByUserId } = useFirebase();

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric"
      });
    } catch {
      return "Invalid date";
    }
  };

  useEffect(() => {
    const fetchCourse = async () => {
      if (!courseId) return;
      
      try {
        setLoading(true);
        const courseData = await getCourseById(courseId);
        setCourse(courseData);
      } catch (error) {
        console.error("Error fetching course:", error);
        toast({
          title: "Error",
          description: "Failed to load course details. Please try again.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchCourse();
  }, [courseId, toast]);

  useEffect(() => {
    const fetchEnrollment = async () => {
      if (!courseId || !currentUser) return;
      
      try {
        setLoading(true);
        const enrollments = await getEnrollmentsByUserId(currentUser.uid);
        const relevantEnrollment = enrollments.find(e => e.courseId === courseId);
        
        if (relevantEnrollment) {
          console.log("Found enrollment:", relevantEnrollment);
          setEnrollment(relevantEnrollment as Enrollment);
        }
      } catch (error) {
        console.error("Error fetching enrollment:", error);
        toast({
          title: "Error",
          description: "Failed to load enrollment details. Please try again.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchEnrollment();
  }, [courseId, currentUser, getEnrollmentsByUserId, toast]);

  const handleBookNow = () => {
    navigate(`/student-dashboard/practical-course/${courseId}/enroll`);
  };

  const getProgressPercentage = () => {
    if (!enrollment?.totalHours) return 0;
    return Math.round((enrollment.hoursTaken || 0) / enrollment.totalHours * 100);
  };

  const getHoursRemaining = () => {
    if (!enrollment?.totalHours) return 0;
    return enrollment.totalHours - (enrollment.hoursTaken || 0);
  };

  const canScheduleMoreClasses = () => {
    return getHoursRemaining() > 0;
  };

  const handleScheduleClass = () => {
    setIsSchedulingOpen(true);
  };

  if (loading) {
    return (
      <DashboardLayout role="student">
        <div className="flex justify-center items-center h-64">
          <p>Loading course details...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!course) {
    return (
      <DashboardLayout role="student">
        <div className="text-center py-12">
          <h2 className="text-xl font-bold mb-2">Course Not Found</h2>
          <p className="text-gray-500 mb-4">The course you're looking for could not be found.</p>
          <Button onClick={() => navigate("/student-dashboard/courses")}>
            Browse Courses
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="student">
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">{course.title}</h1>
          <p className="text-gray-500 mt-2">
            {enrollment ? "Your booked practical driving course" : "Explore the details of our practical driving course"}
          </p>
        </div>

        {enrollment ? (
          <>
            <div className="grid gap-6 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>Your Booking Details</CardTitle>
                  <CardDescription>View the details of your scheduled practical course</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-blue-50 p-6 rounded-lg">
                    <div className="mb-3 flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-blue-500" />
                      <span className="font-medium">Registered Date:</span> 
                      <span>{formatDate(enrollment.startDate)}</span>
                    </div>
                    
                    <div className="mb-3 flex items-center gap-2">
                      <Clock className="h-5 w-5 text-blue-500" />
                      <span className="font-medium">Preferred Time:</span> 
                      <span>{enrollment.preferredTime || "Not specified"}</span>
                    </div>
                    
                    {enrollment.instructorName ? (
                      <div className="mb-3 flex items-center gap-2">
                        <User className="h-5 w-5 text-blue-500" />
                        <span className="font-medium">Instructor:</span> 
                        <span>{enrollment.instructorName}</span>
                      </div>
                    ) : (
                      <div className="mb-3 text-amber-600">
                        An instructor will be assigned to your booking soon.
                      </div>
                    )}
                    
                    <Separator className="my-3" />
                    
                    <div className="mt-4">
                      <h4 className="font-medium mb-2">Your Information:</h4>
                      {enrollment.drivingExperience && (
                        <p className="text-sm mb-2">
                          <span className="font-medium">Driving Experience:</span> {enrollment.drivingExperience}
                        </p>
                      )}
                      {enrollment.vehicleType && (
                        <p className="text-sm mb-2">
                          <span className="font-medium">Vehicle Type:</span> {enrollment.vehicleType}
                        </p>
                      )}
                      {enrollment.additionalNotes && (
                        <p className="text-sm">
                          <span className="font-medium">Additional Notes:</span> {enrollment.additionalNotes}
                        </p>
                      )}
                    </div>
                    
                    <div className="mt-4 flex items-center">
                      <span className="font-medium mr-2">Status:</span>
                      <span className={`px-2 py-1 rounded text-xs ${
                        enrollment.status === 'assigned' ? 'bg-green-100 text-green-800' : 
                        enrollment.status === 'completed' ? 'bg-blue-100 text-blue-800' : 
                        'bg-amber-100 text-amber-800'
                      }`}>
                        {enrollment.status === 'assigned' ? 'Instructor Assigned' : 
                         enrollment.status === 'completed' ? 'Completed' : 
                         'Pending Assignment'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 mt-4">
                    <span className="text-xl font-bold">£{course.price ? course.price.toFixed(2) : "299.00"}</span>
                    <span className="text-gray-500 ml-2">Paid</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Course Progress</CardTitle>
                  <CardDescription>Track your practical driving lessons</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="font-medium text-gray-700">Completion Status</span>
                      <span className="font-bold">{getProgressPercentage()}%</span>
                    </div>
                    <Progress value={getProgressPercentage()} className="h-2" />
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h4 className="text-sm font-medium text-green-700">Hours Completed</h4>
                      <p className="text-2xl font-bold text-green-800 mt-1">{enrollment.hoursTaken || 0}</p>
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="text-sm font-medium text-blue-700">Hours Remaining</h4>
                      <p className="text-2xl font-bold text-blue-800 mt-1">{getHoursRemaining()}</p>
                    </div>
                  </div>

                  <Card className="mt-6 border border-dashed">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">Schedule Another Lesson</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-500 mb-4">
                        {canScheduleMoreClasses() 
                          ? "You have hours remaining in your package. Schedule your next driving lesson."
                          : "You've used all allocated hours in this package. Contact us to purchase additional hours."}
                      </p>
                      <Button 
                        onClick={handleScheduleClass} 
                        disabled={!canScheduleMoreClasses()} 
                        className="w-full"
                        variant="default"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Schedule Next Lesson
                      </Button>
                    </CardContent>
                  </Card>
                </CardContent>
              </Card>
            </div>

            {enrollment.upcomingSchedules && enrollment.upcomingSchedules.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Upcoming Classes</CardTitle>
                  <CardDescription>Your scheduled driving lessons</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {enrollment.upcomingSchedules.map((schedule, index) => (
                      <div key={index} className="flex items-center justify-between border-b last:border-0 pb-4 last:pb-0">
                        <div className="flex items-center gap-3">
                          <div className="bg-blue-100 p-2.5 rounded-full">
                            <Calendar className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium">{format(new Date(schedule.date), 'EEEE, MMMM d, yyyy')}</p>
                            <p className="text-sm text-gray-500">{schedule.time}</p>
                          </div>
                        </div>
                        <Badge>{schedule.status}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            
            <ScheduleClassDialog 
              open={isSchedulingOpen} 
              onOpenChange={setIsSchedulingOpen}
              courseId={courseId}
              enrollmentId={enrollment.id}
              hoursRemaining={getHoursRemaining()}
              onScheduled={() => {
                toast({
                  title: "Class Scheduled",
                  description: "Your driving lesson has been scheduled successfully."
                });
              }}
            />
          </>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>{course.title}</CardTitle>
              <CardDescription>{course.description}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-blue-500" />
                <span>{course.duration || 5} driving lessons</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xl font-bold">£{course.price ? course.price.toFixed(2) : "299.00"}</span>
              </div>
              <Button onClick={handleBookNow}>Book Now</Button>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default PracticalCourseDetails;
